import React from 'react';

import { useDispatch } from 'react-redux';

import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Checkbox from '@amzn/meridian/checkbox';
import Column from '@amzn/meridian/column';
import Input, { type InputProps } from '@amzn/meridian/input/input';
import Row, { type RowProps } from '@amzn/meridian/row/row';
import Text from '@amzn/meridian/text';
import { type EmailSettings } from 'src/services/emailDistribution/types/emailSettings';
import { setEmailSettingsFormData } from 'src/store/emailDistributionPageSlice';
import { useAppSelector } from 'src/store/store';

const EmailDistributionFormFields = () => {
  const dispatch = useDispatch();
  const { emailSettingsFormData, pageDisabled, emailLength } = useAppSelector((state) => state.emailDistributionPage);
  const { featureFlags } = useAppSelector((state) => state.user);

  const updateFormValue = (emailSetting: keyof EmailSettings, newValue: string | boolean) => {
    dispatch(
      setEmailSettingsFormData({
        ...emailSettingsFormData,
        [emailSetting]: newValue,
      }),
    );
  };

  // compatible with onChange prop with no need for intermediary lambda/arrow function
  const getOnChangeHandler = (emailSetting: keyof EmailSettings) => {
    return (newValue: string | boolean) => updateFormValue(emailSetting, newValue);
  };

  const getRemoveButtonWhenPageEnabled = (emailSetting: keyof EmailSettings) => {
    return pageDisabled ? (
      <Box /> /* Empty Box when disabled so the Input Column doesn't expand to fill the whole width of the Row */
    ) : (
      <Button type="link" onClick={() => updateFormValue(emailSetting, '')}>
        Remove
      </Button>
    );
  };

  const distroRowProps: RowProps = {
    widths: ['grid-3', 'fill', 'grid-1'],
    alignmentVertical: 'baseline',
  };

  const distroInputCommonProps: InputProps = {
    type: 'text',
    placeholder: 'Enter ; separated list of aliases or email addresses here',
    disabled: pageDisabled,
  };

  return (
    <Column spacing="500">
      <Row {...distroRowProps}>
        <Text tag="label" type="b300" color="primary" htmlFor="rejected-placement-email-recipients-input">
          Email distros for 'Rejected Placement'.
        </Text>
        <Column>
          <Input
            {...distroInputCommonProps}
            value={emailSettingsFormData?.deniedEmailRecipients}
            onChange={getOnChangeHandler('deniedEmailRecipients')}
            id="rejected-placement-email-recipients-input"
            data-cy="rejected-placement-email-recipients__input"
            constraintText={`${emailSettingsFormData.deniedEmailRecipients.length} / ${emailLength} characters`}
          />

          <Checkbox
            checked={emailSettingsFormData.supervisorsForDeniedEmail || false}
            onChange={getOnChangeHandler('supervisorsForDeniedEmail')}
            disabled={pageDisabled}
            data-cy="auto-include-aa-supervisor-rejected__checkbox"
          >
            Auto-include AA's supervisor?
          </Checkbox>
        </Column>
        {getRemoveButtonWhenPageEnabled('deniedEmailRecipients')}
      </Row>
      <Row {...distroRowProps}>
        <Text tag="label" type="b300" color="primary" htmlFor="approved-email-recipients">
          Email distros for 'Approved Onsite Placement'.
        </Text>
        <Column>
          <Input
            {...distroInputCommonProps}
            value={emailSettingsFormData?.placementEmailRecipients}
            onChange={getOnChangeHandler('placementEmailRecipients')}
            id="approved-email-recipients"
            data-cy="approved-email-recipients__input"
            constraintText={`${emailSettingsFormData.placementEmailRecipients.length} / ${emailLength} characters`}
          />
          <Checkbox
            checked={emailSettingsFormData.supervisorsForPlacementEmail || false}
            onChange={getOnChangeHandler('supervisorsForPlacementEmail')}
            disabled={pageDisabled}
            data-cy="auto-include-aa-supervisor-approved__checkbox"
          >
            Auto-include AA's supervisor?
          </Checkbox>
        </Column>
        {getRemoveButtonWhenPageEnabled('placementEmailRecipients')}
      </Row>
      <Row {...distroRowProps}>
        <Text tag="label" type="b300" color="primary" htmlFor="loa-email-recipients">
          Email distros for 'No Onsite Placement LOA'.
        </Text>
        <Column>
          <Input
            {...distroInputCommonProps}
            value={emailSettingsFormData?.loaEmailRecipients}
            onChange={getOnChangeHandler('loaEmailRecipients')}
            id="loa-email-recipients"
            data-cy="loa-email-recipients__input"
            constraintText={`${emailSettingsFormData.loaEmailRecipients.length} / ${emailLength} characters`}
          />
          <Checkbox
            checked={emailSettingsFormData?.supervisorsForLoaEmail || false}
            onChange={getOnChangeHandler('supervisorsForLoaEmail')}
            disabled={pageDisabled}
            data-cy="auto-include-aa-supervisor-loa__checkbox"
          >
            Auto-include AA's supervisor?
          </Checkbox>
        </Column>
        {getRemoveButtonWhenPageEnabled('loaEmailRecipients')}
      </Row>
      <Row {...distroRowProps}>
        <Text tag="label" type="b300" color="primary" htmlFor="rtfd-email-recipients">
          Email distros for 'Returned to Full Duty'.
        </Text>
        <Column>
          <Input
            {...distroInputCommonProps}
            value={emailSettingsFormData?.rtfdEmailRecipients}
            onChange={getOnChangeHandler('rtfdEmailRecipients')}
            id="rtfd-email-recipients"
            data-cy="rtfd-email-recipients__input"
            constraintText={`${emailSettingsFormData.rtfdEmailRecipients.length} / ${emailLength} characters`}
          />
          <Checkbox
            checked={emailSettingsFormData.supervisorsForRtfdEmail || false}
            onChange={getOnChangeHandler('supervisorsForRtfdEmail')}
            disabled={pageDisabled}
            data-cy="auto-include-aa-supervisor-rtfd__checkbox"
          >
            Auto-include AA's supervisor?
          </Checkbox>
        </Column>
        {getRemoveButtonWhenPageEnabled('rtfdEmailRecipients')}
      </Row>
      {featureFlags.CORRESPONDENCE && (
        <Row {...distroRowProps}>
          <Text tag="label" type="b300" color="primary" htmlFor="decisionhub-email-recipients">
            Email distros for 'Decision Hub Decisions'.
          </Text>
          <Column>
            <Input
              {...distroInputCommonProps}
              value={emailSettingsFormData?.decisionHubDecisionEmailRecipients}
              onChange={getOnChangeHandler('decisionHubDecisionEmailRecipients')}
              id="decisionhub-email-recipients"
              data-cy="decisionhub-email-recipients__input"
              constraintText={`${emailSettingsFormData.decisionHubDecisionEmailRecipients.length} / ${emailLength} characters`}
            />
          </Column>
          {getRemoveButtonWhenPageEnabled('decisionHubDecisionEmailRecipients')}
        </Row>
      )}
    </Column>
  );
};

export default EmailDistributionFormFields;
